'use client';

import { SchedulesSearchSlot } from '@/app/(main)/(pages)/_components/schedules/_types/schedule-slot.type';
import { useToast } from '@/components/ui/use-toast';
import { getLogger } from '@/logger/logger';
import { changeSlotOnCartItems } from '@/store/cart/cart.store';
import { openScheduleSelection } from '@/store/schedule-selection/schedule-selection.store';
import { CartItem } from '@/types/cart.types';
import { CartChangeScheduleScope } from '@/types/enums/analytics/cart/cart-analytics.enum';
import { trackChangeScheduleOpened, trackScheduleChanged } from '@/utils/cart/cart-analytics.util';

export const CHANGE_CART_SCHEDULE_ERROR_MESSAGE = 'Não foi possível trocar o horário. Tente novamente.';

const logger = getLogger('cart.change-schedule');

export interface UseChangeCartScheduleResult {
  changeItemSchedule: (item: CartItem) => void;
  changeGroupSchedule: (items: CartItem[]) => void;
}

export function useChangeCartSchedule(): UseChangeCartScheduleResult {
  const { toast } = useToast();

  function requestChange(items: CartItem[], scope: CartChangeScheduleScope): void {
    if (items.length === 0) {
      return;
    }

    trackChangeScheduleOpened(scope, items);

    openScheduleSelection({
      items,
      onSelectSlot: (slot) => {
        void applyChange(items, slot, scope);
      },
    });
  }

  async function applyChange(
    items: CartItem[],
    slot: SchedulesSearchSlot,
    scope: CartChangeScheduleScope,
  ): Promise<void> {
    const cartItemsIds = items.map((item) => item.id);

    try {
      await changeSlotOnCartItems(cartItemsIds, {
        id: slot.id,
        start: slot.start,
        end: slot.end,
        date: slot.date,
      });

      trackScheduleChanged(scope, items, slot);
    } catch (error) {
      logger.error('Failed to change the schedule on cart items', {
        cart: {
          event: 'schedule_change_failed',
          scope,
          cart_item_ids: cartItemsIds,
          slot_id: slot.id,
        },
        error,
      });
      toast.error(CHANGE_CART_SCHEDULE_ERROR_MESSAGE);
    }
  }

  return {
    changeItemSchedule: (item: CartItem) => requestChange([item], CartChangeScheduleScope.ITEM),
    changeGroupSchedule: (items: CartItem[]) => requestChange(items, CartChangeScheduleScope.GROUP),
  };
}
